import { Paper, Title, Text, Stack, Group, Loader, Center, Progress } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { queryPrometheus } from './prometheusApi';
import { useRefreshInterval } from './RefreshContext';

interface ContainerUsage {
  pod: string;
  cpu: number;
  memoryMb: number;
}

interface MetricResult {
  metric: { pod: string; [key: string]: string | undefined };
  value: [number, string];
}

function useTopConsumers() {
  const interval = useRefreshInterval();
  return useQuery({
    queryKey: ['cockpit', 'top-consumers'],
    queryFn: async (): Promise<ContainerUsage[]> => {
      const [cpuData, memData] = await Promise.all([
        queryPrometheus('sum by (pod) (rate(container_cpu_usage_seconds_total{namespace="default",pod!=""}[5m]))') as Promise<MetricResult[]>,
        queryPrometheus('sum by (pod) (container_memory_working_set_bytes{namespace="default",pod!=""})') as Promise<MetricResult[]>,
      ]);

      const memMap = new Map<string, number>();
      for (const m of memData) {
        memMap.set(m.metric.pod, Number(m.value[1]));
      }

      return cpuData
        .map((c) => ({
          pod: c.metric.pod,
          cpu: Number(c.value[1]),
          memoryMb: (memMap.get(c.metric.pod) ?? 0) / 1e6,
        }))
        .sort((a, b) => b.cpu - a.cpu)
        .slice(0, 8);
    },
    refetchInterval: interval,
  });
}

export function TopConsumersPanel() {
  const { data, isLoading } = useTopConsumers();

  if (isLoading || !data) {
    return (
      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="md">Top Verbraucher</Title>
        <Center><Loader size="sm" /></Center>
      </Paper>
    );
  }

  const maxCpu = Math.max(...data.map(d => d.cpu), 0.01);

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Title order={4} mb="md">Top Verbraucher</Title>
      <Stack gap="xs">
        {data.map((item) => (
          <Stack key={item.pod} gap={2}>
            <Group justify="space-between">
              <Text size="xs" lineClamp={1} style={{ maxWidth: '60%' }}>{item.pod}</Text>
              <Text size="xs" c="dimmed">CPU: {(item.cpu * 1000).toFixed(0)}m · RAM: {item.memoryMb.toFixed(0)} MB</Text>
            </Group>
            <Progress value={(item.cpu / maxCpu) * 100} size="xs" color="blue" />
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}
